// src/pages/DebtStudio/components/ScaleBuilder/ScaleGrid.jsx
import React, { useMemo, useCallback } from "react";
import ExcelGrid from "components/ExcelGrid";
import dayjs from "lib/dayjs";

// Small helper: format number safely
const fmt = (val, decimals) => {
  const n = Number(val);
  return isNaN(n) ? "" : n.toFixed(decimals);
};

const formatUiDate = (v) => {
  if (!v) return "";
  const d = dayjs(v);
  return d.isValid() ? d.format("MM/DD/YYYY") : "";
};

// ---- MAIN ScaleGrid ----
export default function ScaleGrid({
  rows,
  onBpsChange = () => {},
  onCouponOverrideChange = () => {},
}) {
  // Prepare rows for display only
  const gridRows = useMemo(
    () =>
      rows.map((r) => ({
        ...r,
        coupon_display: r.coupon ?? "", // always derived from canonical coupon
        adj_display: r.adjustment_bps ?? 0,
      })),
    [rows]
  );

  // Define columns for the grid
  const columns = [
    {
      key: "date",
      label: "Date",
      editable: false,
      width: "w-28",
      className: "numeric",
      format: (v) => formatUiDate(v),
      copyValue: (row) => formatUiDate(row.date),
    },
    {
      key: "coupon_display",
      label: "Coupon (%)",
      editable: true,
      numeric: true,
      width: "w-20",
      className: "text-right numeric",
      headerClassName: "text-right",
      format: (v) => fmt(v, 3),
    },
    {
      key: "benchmark",
      label: "Interpolated curve (%)",
      editable: false,
      numeric: true,
      width: "w-28",
      className: "text-right numeric",
      headerClassName: "text-right",
      format: (v) => fmt(v, 2),
    },
    {
      key: "adj_display",
      label: "Spread (bps)",
      editable: true,
      numeric: true,
      width: "w-20",
      className: "text-right numeric",
      headerClassName: "text-right",
    },
    {
      key: "yield",
      label: "Yield (%)",
      editable: false,
      numeric: true,
      width: "w-20",
      className: "text-right numeric",
      headerClassName: "text-right",
      format: (v) => fmt(v, 2),
    },
    {
      key: "price",
      label: "Price",
      editable: false,
      numeric: true,
      width: "w-24",
      className: "text-right numeric",
      headerClassName: "text-right",
      format: (v) => fmt(v, 8),
    },
  ];

  // ---- Unified edit handler (with debug logging) ----
  const handleGridChange = useCallback(
    (newRows) => {
      newRows.forEach((r, idx) => {
        const old = gridRows[idx];
        if (!old) return;

        // Coupon edit → push back as canonical coupon
        if (
          r.coupon_display !== old.coupon_display &&
          r.coupon_display !== "" &&
          r.date
        ) {
          const n = Number(r.coupon_display);
          onCouponOverrideChange(r.date, isNaN(n) ? null : n);
        }

        // Spread edit → push back as canonical adjustment_bps
        if (r.adj_display !== old.adj_display && r.date) {
          const n = Number(r.adj_display);
          onBpsChange(r.date, isNaN(n) ? 0 : Math.round(n));
        }
      });
    },
    [onBpsChange, onCouponOverrideChange, gridRows]
  );

  return (
    <div className="overflow-x-auto pb-2">
      <ExcelGrid
        columns={columns}
        rows={gridRows}
        onChange={handleGridChange}
      />
    </div>
  );
}